import {redirect, createFileRoute} from "@tanstack/react-router";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {z as zod} from "zod";
import {jsonZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {HEIMDALL_LOCAL_STORAGE_KEY} from "~/utilities/constants";
import {consumePostLoginRedirectPathForOidcState} from "~/utilities/postLoginRedirect";

const OIDC_CODE_VERIFIER_KEY = "oidc_code_verifier";
const OIDC_STATE_KEY = "oidc_state";
const OIDC_CALLBACK_PROMISE_TTL_MS = 60_000;

const oidcCallbackPromisesByState = new Map<string, Promise<never>>();

const tokenResponseSchema = zod.object({
    access_token: zod.string(),
    refresh_token: zod.string().optional(),
    token_type: zod.string(),
    expires_in: zod.number().optional(),
});
const userInfoResponseSchema = jsonZodValidator.pipe(
    zod.object({
        sub: uuidZodValidator,
    }),
);

export const Route = createFileRoute("/_withAuthenticationStatus/_notAuthenticated/auth-callback")({
    validateSearch: zod.object({
        code: zod.string(),
        state: zod.string(),
    }),
    beforeLoad: async ({context, search}) => {
        const {code, state} = search;

        const existingCallbackPromise = oidcCallbackPromisesByState.get(state);
        if (existingCallbackPromise !== undefined) {
            return await existingCallbackPromise;
        }

        const callbackPromise = completeOidcCallback({
            code,
            context,
            state,
        });
        oidcCallbackPromisesByState.set(state, callbackPromise);

        try {
            return await callbackPromise;
        } finally {
            setTimeout(() => {
                oidcCallbackPromisesByState.delete(state);
            }, OIDC_CALLBACK_PROMISE_TTL_MS);
        }
    },
    loader: async () => {},
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});

async function completeOidcCallback({
    code,
    context,
    state,
}: {
    code: string;
    context: {
        intellsysBaseUrl: string;
        oidcClientId: string;
    };
    state: string;
}): Promise<never> {
    const storedState = sessionStorage.getItem(OIDC_STATE_KEY);
    const codeVerifier = sessionStorage.getItem(OIDC_CODE_VERIFIER_KEY);

    if (storedState === null || codeVerifier === null) {
        throw new Error("Missing PKCE session state. Please sign in again.");
    }
    if (state !== storedState) {
        throw new Error("State mismatch. Please sign in again.");
    }

    const tokenResponse = await fetch(`${context.intellsysBaseUrl}/oauth/token`, {
        method: "POST",
        credentials: "include",
        headers: {"Content-Type": "application/x-www-form-urlencoded"},
        body: new URLSearchParams({
            grant_type: "authorization_code",
            code: code,
            code_verifier: codeVerifier,
            client_id: context.oidcClientId,
            redirect_uri: `${context.intellsysBaseUrl}/auth-callback`,
        }).toString(),
    });

    if (tokenResponse.ok !== true) {
        const body = await tokenResponse.text();
        throw new Error(`Token exchange failed (${String(tokenResponse.status)}): ${body}`);
    }

    const tokenResult = tokenResponseSchema.safeParse(await tokenResponse.json());
    if (tokenResult.success !== true) {
        throw new Error("Unexpected token response shape.");
    }

    const {access_token, refresh_token} = tokenResult.data;
    const userInfoResponse = await fetch(`${context.intellsysBaseUrl}/oauth/userinfo`, {
        method: "GET",
        credentials: "include",
        headers: {
            Authorization: `Bearer ${access_token}`,
        },
    });
    const userInfoBody = await userInfoResponse.text();
    const userInfoResult = userInfoResponseSchema.safeParse(userInfoBody);
    if (userInfoResponse.ok !== true || userInfoResult.success !== true) {
        throw new Error(
            `Signed in, but /oauth/userinfo did not return a user (${String(userInfoResponse.status)}): ${userInfoBody}`,
        );
    }

    const postLoginRedirectPath = consumePostLoginRedirectPathForOidcState({
        intellsysBaseUrl: context.intellsysBaseUrl,
        state: state,
    });

    const heimdallValue = {
        session: {
            accessToken: access_token,
            ...(refresh_token !== undefined ? {refreshToken: refresh_token} : {}),
        },
    };
    localStorage.setItem(HEIMDALL_LOCAL_STORAGE_KEY, JSON.stringify(heimdallValue));
    sessionStorage.removeItem(OIDC_CODE_VERIFIER_KEY);
    sessionStorage.removeItem(OIDC_STATE_KEY);

    throw redirect({
        href: postLoginRedirectPath,
    });
}
